import React, { useState, useRef } from 'react';
import useTheme from '@cvent/blocks/hooks/useTheme';
import useInterval from '@hooks/useInterval';
import { useTranslate } from 'nucleus-text';
import updateSessions from '@tools/updateSessions';
import { useEnrolledSessions } from '@context/EnrolledSessionsContext';
import { useQuery } from '@apollo/client';
import getUpcomingSessionsSearchCriteria from '@tools/getUpcomingSessionsSearchCriteria';
import { startOfMinute, addMonths } from 'date-fns';
import useLocaleForQueries from '@hooks/useLocaleForQueries';
import { CSSObject } from '@emotion/react';
import NavigationFlyoutControl from '@components/header/flyouts/NavigationFlyoutControl';
import { AgendaPaginatedResults } from '@graphql/gql/graphql';
import { SEARCH_SCHEDULE_LIMIT } from '@components/mySchedule/constants';
import useMeQuery from '@hooks/useMeQuery';
import useAppFeatures from '@hooks/useAppFeatures';
import Icon from '@components/shared/Icon';
import { SEARCH_SESSIONS } from '@graphql/operations/sessions/searchSessions';
import { SEARCH_SCHEDULE } from '@graphql/operations/schedule/searchSchedule';
import stripCurrentSession from './stripCurrentSession';
import MyScheduleFlyoutContent from './MyScheduleFlyoutContent';
import stripCurrentSessionOnEndTime from './stripCurrentSessionOnEndTime';

const offset = 300000;

interface Props {
  eventId: string;
  appointmentsEnabled?: boolean;
  hasSessionList?: boolean;
  navHeight?: number;
  admissionItemId: string;
}

const useStyles = (() => {
  const { font } = useTheme();

  return {
    icon: {
      color: font.color.base
    }
  };
}) satisfies () => Record<string, CSSObject>;

function MyScheduleFlyout({
  eventId,
  appointmentsEnabled,
  hasSessionList,
  navHeight,
  admissionItemId
}: Props): JSX.Element {
  const styles = useStyles();
  const { translate } = useTranslate();
  const [isFlyoutVisible, setIsFlyoutVisible] = useState(false);
  const [now, setNow] = useState(Date.now());
  const [queryNow, setQueryNow] = useState(Date.now());
  const previousToken = useRef<string>();
  const { enrolledSessions } = useEnrolledSessions();
  const locale = useLocaleForQueries();
  const { showEndedScheduleFlyout } = useAppFeatures();

  // Checks every 30secs to see if the already-loaded agenda and session lists are showing the correct result
  useInterval(() => setNow(Date.now()), 30000);
  useInterval(() => setQueryNow(Date.now()), 300000); // 5 minutes

  // TODO: Remove date/time filtering of sessions when HUB-29925 is fixed
  // Current bug in universal-attendee is returning sessions whose END times are after the
  // supplied start variable instead of only those whose START times are after it
  const {
    data,
    loading: scheduleLoading,
    fetchMore
  } = useQuery<{ myAgenda: AgendaPaginatedResults }>(SEARCH_SCHEDULE, {
    variables: {
      criteria: {
        pageCriteria: { limit: SEARCH_SCHEDULE_LIMIT.UPCOMING },
        start: startOfMinute(queryNow),
        locale
      },
      virtualAttendee: false,
      enableSessionCapacity: false,
      enableSessionFee: false
    },
    skip: !isFlyoutVisible,
    notifyOnNetworkStatusChange: true
  });

  const onLoadMore = async () => {
    const nextToken = data?.myAgenda?.pagination?.nextToken;
    if (nextToken && nextToken !== previousToken.current) {
      previousToken.current = nextToken;
      fetchMore({
        variables: {
          criteria: {
            start: startOfMinute(queryNow),
            locale,
            pageCriteria: {
              limit: 20,
              pageToken: data?.myAgenda?.pagination?.nextToken
            }
          }
        }
      });
    }
  };

  let updatedSessions = [];
  if (data?.myAgenda?.data.length === 0) {
    updatedSessions = enrolledSessions.filter(session => session.enrolled || session.included);
  } else {
    updatedSessions = updateSessions(data?.myAgenda?.data, enrolledSessions).filter(session => {
      if (appointmentsEnabled && (session.type === 'APPOINTMENT' || session.type === 'PERSONAL_TIME')) {
        return true;
      }
      return session.enrolled || session.included;
    });
  }

  // give attendees five minutes after session start to navigate to their session
  const offsetNow = now - offset;

  const upcomingAgendaItems = updatedSessions.length > 0 ? stripCurrentSession(updatedSessions, offsetNow) : [];
  const upcomingAndDuringAgendaItems =
    updatedSessions.length > 0 ? stripCurrentSessionOnEndTime(updatedSessions, now) : [];
  const { data: sessionsData, loading: sessionsLoading } = useQuery(SEARCH_SESSIONS, {
    variables: {
      criteria: {
        ...getUpcomingSessionsSearchCriteria(now),
        admissionItemId,
        timeframes: showEndedScheduleFlyout
          ? [{ end: addMonths(startOfMinute(queryNow), 3) }]
          : [{ start: startOfMinute(queryNow) }],
        locale
      },
      enableSessionCapacity: false
    },
    skip: !isFlyoutVisible || upcomingAgendaItems?.length > 0 || !admissionItemId
  });

  const areAllSessionsAndAppointmentsOver =
    !upcomingAndDuringAgendaItems.length && !sessionsData?.searchSessions?.data.length;
  const upcomingSessions = stripCurrentSession(sessionsData?.searchSessions.data, now);
  const totalCount = data?.myAgenda?.pagination?.totalCount || 0;
  const limit = data?.myAgenda?.pagination?.limit || 0;

  const { data: userData } = useMeQuery();
  const isOnDemandContentPresent = userData?.me?.event?.features?.onDemandCatalog;
  const endedSchduleFlyoutVisible = showEndedScheduleFlyout && areAllSessionsAndAppointmentsOver;

  return (
    <NavigationFlyoutControl
      accessibilityLabel={translate('MySchedule-Flyout-Header')}
      icon={<Icon name="calendar" size="md" color={styles.icon.color.toString()} aria-hidden />}
      testID="schedule-icon"
      isFlyoutVisible={isFlyoutVisible}
      onFlyoutVisible={setIsFlyoutVisible}
      title={translate('MySchedule-Flyout-Title')}
    >
      <MyScheduleFlyoutContent
        testID={endedSchduleFlyoutVisible ? 'my-ended-schedule-flyout' : 'my-schedule-flyout'}
        upcomingAgendaItems={upcomingAgendaItems}
        agendaLoading={scheduleLoading || sessionsLoading}
        eventId={eventId}
        navHeight={navHeight}
        hasSessionList={hasSessionList}
        hasMore={totalCount - limit > 0}
        onLoadMore={onLoadMore}
        onClose={() => setIsFlyoutVisible(false)}
        upcomingSessions={upcomingSessions}
        endedSchduleFlyoutVisible={endedSchduleFlyoutVisible}
        isOnDemandContentPresent={!!isOnDemandContentPresent}
        isFlyoutVisible={isFlyoutVisible}
      />
    </NavigationFlyoutControl>
  );
}

export default MyScheduleFlyout;
